import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import { connect } from "react-redux";
import {
  async_fetch_events,
  sendComplementaryTicket,
} from "src/actions/event.action";
import EventListResults from "src/components/events/EventListResults";

const TOKENS = {
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  accent: "#222222",
  accentDark: "#000000",
  border: "#EEF0F5",
};

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: "#F5F6FA",
    minHeight: "100%",
    paddingTop: 24,
    paddingBottom: 48,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: TOKENS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 14,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  headerActions: {
    display: "flex",
    gap: 10,
  },
  primaryButton: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": { backgroundColor: TOKENS.accentDark },
  },
  outlinedButton: {
    color: TOKENS.accent,
    borderColor: TOKENS.border,
    borderRadius: 8,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": { borderColor: "#A5B4FC", backgroundColor: "#EEF2FF" },
  },
  dialogPaper: {
    borderRadius: 16,
    border: `1px solid ${TOKENS.border}`,
    boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
  },
  dialogTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: TOKENS.textPrimary,
    padding: "20px 24px 8px 24px",
  },
  dialogContent: {
    padding: "12px 24px 24px 24px",
  },
  dialogActions: {
    padding: "12px 24px 20px 24px",
    gap: 8,
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#A5B4FC" },
      "&.Mui-focused fieldset": { borderColor: TOKENS.accent },
    },
    "& .MuiInputLabel-root": { fontSize: 14, color: TOKENS.textSecondary },
  },
  cancelBtn: {
    color: TOKENS.textSecondary,
    borderRadius: 8,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 500,
    textTransform: "none",
  },
  submitBtn: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "8px 24px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": { backgroundColor: TOKENS.accentDark },
  },
}));

function EventsList({ async_fetch_events, events, sendComplementaryTicket }) {
  const classes = useStyles();

  useEffect(() => {
    async_fetch_events();
  }, [async_fetch_events]);

  const [open, setOpen] = useState(false);
  const [formState, setFormState] = useState({});

  const handleToggle = () => setOpen(!open);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <Helmet>
        <title>Tubayo Host | Events</title>
      </Helmet>
      <Box className={classes.root}>
        <Container maxWidth={false}>
          <Box className={classes.pageHeader}>
            <Box>
              <Typography className={classes.pageTitle}>Events</Typography>
              <Typography className={classes.pageSubtitle}>
                Manage your listed events
              </Typography>
            </Box>
            <Box className={classes.headerActions}>
              <Button
                variant="outlined"
                className={classes.outlinedButton}
                startIcon={<CardGiftcardIcon style={{ fontSize: 18 }} />}
                onClick={handleToggle}
              >
                Complementary Ticket
              </Button>
            </Box>
          </Box>

          <EventListResults events={events} />

          {/* Complementary Ticket Dialog */}
          <Dialog
            open={open}
            onClose={handleToggle}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              className: classes.dialogPaper,
              component: "form",
              onSubmit: (e) => {
                e.preventDefault();
                const data = {
                  ...formState,
                  numberOfTickets: 1,
                  ticketPrice: 0,
                  ticket: "Complementary",
                };
                sendComplementaryTicket(data);
                handleToggle();
              },
            }}
          >
            <DialogTitle disableTypography className={classes.dialogTitle}>
              Send Complementary Ticket
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    className={classes.textField}
                    autoFocus
                    required
                    variant="outlined"
                    fullWidth
                    name="name"
                    label="Full Name"
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    className={classes.textField}
                    required
                    variant="outlined"
                    fullWidth
                    name="phone_number"
                    label="Phone Number"
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    className={classes.textField}
                    required
                    variant="outlined"
                    fullWidth
                    name="email"
                    label="Email Address"
                    type="email"
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    className={classes.textField}
                  >
                    <InputLabel>Event Name</InputLabel>
                    <Select
                      onChange={handleChange}
                      label="Event Name"
                      inputProps={{ name: "eventPaidFor" }}
                      style={{ borderRadius: 8 }}
                    >
                      {events.map((event) => (
                        <MenuItem key={event.id} value={event.event_name}>
                          {event.event_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button className={classes.cancelBtn} onClick={handleToggle}>
                Cancel
              </Button>
              <Button className={classes.submitBtn} type="submit">
                Send Ticket
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </>
  );
}

const mapStateToProps = (state) => ({
  events: state.eventReducer.events,
});

export default connect(mapStateToProps, {
  async_fetch_events,
  sendComplementaryTicket,
})(EventsList);
